/**
 * S7.5: Email Delivery Service using Resend
 * Handles transactional emails for alerts, notifications, and GDPR communications.
 * 
 * Brand Colors:
 * - Primary (fresh-sky): #1babe4
 * - Secondary (steel-azure): #1b7ce4
 * - Background: #F8FAFC
 * - Text: #1e293b
 * - Muted: #475569
 * 
 * Fonts: Outfit (headings), Inter (body)
 */

interface SendEmailParams {
  to: string | string[];
  subject: string;
  html: string;
  text?: string;
  replyTo?: string;
}

interface EmailResult {
  success: boolean;
  id?: string;
  error?: string;
}

const FROM_EMAIL = process.env.NOTIFICATION_FROM_EMAIL || 'Externaliza2 <notificaciones@externaliza2.com>';

// Logo URL hosted on imgur
const LOGO_URL = 'https://i.imgur.com/eFcXMsV.png';

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || 'https://externaliza2.gabsdev.com';

// Brand colors
const COLORS = {
  primary: '#1babe4',      // fresh-sky-500
  primaryLight: '#e8f7fc', // fresh-sky-50
  secondary: '#1b7ce4',    // steel-azure-500
  secondaryLight: '#e8f2fc', // steel-azure-50
  background: '#F8FAFC',   // calm-bg
  text: '#1e293b',         // calm-text
  muted: '#475569',        // calm-muted
  border: '#E2E8F0',       // calm-border
  white: '#FFFFFF',
  success: '#059669',
  warning: '#d97706',
  error: '#dc2626',
  info: '#2563eb',
};

/**
 * Base email template with Externaliza2 branding
 */
function baseEmailTemplate(options: {
  title: string;
  content: string;
  footer?: string;
  headerColor?: string;
}): string {
  const { title, content, footer, headerColor = COLORS.primary } = options;
  
  return `
<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title}</title>
  <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500&family=Inter:wght@300;400;500&display=swap" rel="stylesheet">
  <style>
    @media only screen and (max-width: 600px) {
      .container { width: 100% !important; padding: 10px !important; }
      .content { padding: 20px !important; }
      .header { padding: 20px !important; }
    }
  </style>
</head>
<body style="margin: 0; padding: 0; background-color: ${COLORS.background}; font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; -webkit-font-smoothing: antialiased;">
  <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
    <tr>
      <td align="center" style="padding: 40px 20px;">
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="600" class="container" style="max-width: 600px; width: 100%;">
          
          <!-- Logo/Header -->
          <tr>
            <td class="header" style="background: linear-gradient(135deg, ${headerColor} 0%, ${COLORS.secondary} 100%); padding: 32px 40px; border-radius: 16px 16px 0 0; text-align: center;">
              <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
                <tr>
                  <td align="center">
                    <!-- Logo em círculo branco perfeito -->
                    <table role="presentation" cellspacing="0" cellpadding="0" border="0" style="margin: 0 auto 16px; width: 88px; height: 88px;">
                      <tr>
                        <td style="background: #FFFFFF; border-radius: 50%; width: 88px; height: 88px; padding: 0; box-shadow: 0 4px 12px rgba(0,0,0,0.15); text-align: center; vertical-align: middle;">
                          <img src="${LOGO_URL}" alt="Externaliza2" width="64" style="display: inline-block; height: auto; max-height: 64px; vertical-align: middle;" />
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
                <tr>
                  <td align="center" style="padding-top: 8px;">
                    <p style="margin: 0; font-size: 13px; color: rgba(255,255,255,0.8); font-weight: 300;">
                      Consultores Asesores
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          
          <!-- Main Content -->
          <tr>
            <td class="content" style="background: ${COLORS.white}; padding: 40px; border-radius: 0 0 16px 16px; box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.02), 0 1px 2px -1px rgb(0 0 0 / 0.02);">
              ${content}
            </td>
          </tr>
          
          <!-- Footer -->
          <tr>
            <td style="padding: 32px 20px; text-align: center;">
              <p style="margin: 0 0 8px; font-size: 12px; color: ${COLORS.muted}; line-height: 1.5;">
                ${footer || 'Este email fue enviado automáticamente por Externaliza2.'}
              </p>
              <p style="margin: 0; font-size: 11px; color: #94a3b8;">
                © ${new Date().getFullYear()} Externaliza2 Consultores S.L. — Todos los derechos reservados
              </p>
              <p style="margin: 16px 0 0;">
              <p style="margin: 16px 0 0;">
                <a href="${SITE_URL}" style="color: ${COLORS.primary}; text-decoration: none; font-size: 12px;">Ir al panel →</a>
              </p>
              </p>
            </td>
          </tr>
          
        </table>
      </td>
    </tr>
  </table>
</body>
</html>
  `.trim();
}

/**
 * Send an email via Resend API.
 * Falls back gracefully if RESEND_API_KEY is not configured.
 */
export async function sendEmail(params: SendEmailParams): Promise<EmailResult> {
  const apiKey = process.env.RESEND_API_KEY;

  if (!apiKey) {
    console.warn('[Email] RESEND_API_KEY not set. Email not sent:', params.subject);
    return { success: false, error: 'RESEND_API_KEY not configured' };
  }

  try {
    const response = await fetch('https://api.resend.com/emails', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        from: FROM_EMAIL,
        to: Array.isArray(params.to) ? params.to : [params.to],
        subject: params.subject,
        html: params.html,
        text: params.text,
        reply_to: params.replyTo,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('[Email] Resend API error:', response.status, errorData);
      return { success: false, error: `Resend API ${response.status}: ${JSON.stringify(errorData)}` };
    }

    const data = await response.json();
    return { success: true, id: data.id };
  } catch (error: any) {
    console.error('[Email] Send failed:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Send a welcome email to new users
 */
export async function sendWelcomeEmail(
  to: string,
  userData: { name: string; role: 'admin' | 'asesor' | 'cliente' }
): Promise<EmailResult> {
  const roleLabels = {
    admin: 'Administrador',
    asesor: 'Asesor',
    cliente: 'Cliente',
  };

  const content = `
    <h2 style="margin: 0 0 24px; font-family: 'Outfit', sans-serif; font-size: 24px; font-weight: 300; color: ${COLORS.text}; letter-spacing: -0.02em;">
      ¡Bienvenido a Externaliza2!
    </h2>
    
    <p style="margin: 0 0 16px; font-size: 16px; color: ${COLORS.text}; line-height: 1.6;">
      Hola ${userData.name},
    </p>
    
    <p style="margin: 0 0 24px; font-size: 16px; color: ${COLORS.muted}; line-height: 1.6;">
      Tu cuenta ha sido creada exitosamente como <strong style="color: ${COLORS.primary};">${roleLabels[userData.role]}</strong>. 
      Ahora puedes acceder a todas las funcionalidades de la plataforma.
    </p>
    
    <table role="presentation" cellspacing="0" cellpadding="0" border="0" style="margin: 32px 0;">
      <tr>
        <td style="background: linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%); border-radius: 12px; text-align: center;">
          <a href="${SITE_URL}" style="display: inline-block; padding: 16px 32px; color: ${COLORS.white}; text-decoration: none; font-size: 15px; font-weight: 500; border-radius: 12px;">
            Acceder al Panel
          </a>
        </td>
      </tr>
    </table>
    
    <div style="background: ${COLORS.primaryLight}; border-left: 4px solid ${COLORS.primary}; padding: 16px 20px; border-radius: 0 8px 8px 0; margin: 24px 0;">
      <p style="margin: 0; font-size: 14px; color: ${COLORS.text}; line-height: 1.5;">
        <strong>¿Necesitas ayuda?</strong><br>
        Contacta con nuestro equipo en <a href="mailto:soporte@externaliza2.com" style="color: ${COLORS.primary}; text-decoration: none;">soporte@externaliza2.com</a>
      </p>
    </div>
  `;

  return sendEmail({
    to,
    subject: '¡Bienvenido a Externaliza2!',
    html: baseEmailTemplate({
      title: 'Bienvenido',
      content,
    }),
  });
}

/**
 * Send alert notification email with improved design
 */
export async function sendAlertEmail(
  to: string,
  alert: { severity: 'critical' | 'high' | 'medium' | 'low'; message: string; rule_name?: string; created_at?: string }
): Promise<EmailResult> {
  const severityConfig = {
    critical: { color: COLORS.error, bgColor: '#fef2f2', icon: '🔴', label: 'CRÍTICA' },
    high: { color: '#ea580c', bgColor: '#fff7ed', icon: '🟠', label: 'ALTA' },
    medium: { color: COLORS.warning, bgColor: '#fffbeb', icon: '🟡', label: 'MEDIA' },
    low: { color: COLORS.info, bgColor: '#eff6ff', icon: '🔵', label: 'BAJA' },
  };

  const config = severityConfig[alert.severity];

  const content = `
    <div style="background: ${config.bgColor}; border: 1px solid ${config.color}20; border-radius: 12px; padding: 20px 24px; margin-bottom: 24px;">
      <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
        <tr>
          <td width="40" style="vertical-align: top;">
            <span style="font-size: 24px;">${config.icon}</span>
          </td>
          <td>
            <span style="display: inline-block; background: ${config.color}; color: white; padding: 4px 12px; border-radius: 20px; font-size: 11px; font-weight: 600; letter-spacing: 0.05em; text-transform: uppercase;">
              Alerta ${config.label}
            </span>
          </td>
        </tr>
      </table>
    </div>
    
    <h2 style="margin: 0 0 16px; font-family: 'Outfit', sans-serif; font-size: 20px; font-weight: 400; color: ${COLORS.text}; letter-spacing: -0.01em;">
      ${alert.rule_name || 'Alerta del Sistema'}
    </h2>
    
    <p style="margin: 0 0 24px; font-size: 16px; color: ${COLORS.muted}; line-height: 1.6;">
      ${alert.message}
    </p>
    
    ${alert.created_at ? `
    <p style="margin: 0 0 24px; font-size: 13px; color: #94a3b8;">
      <span style="color: ${COLORS.muted};">Fecha:</span> ${new Date(alert.created_at).toLocaleString('es-ES', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })}
    </p>
    ` : ''}
    
    <table role="presentation" cellspacing="0" cellpadding="0" border="0" style="margin: 32px 0;">
      <tr>
        <td style="border: 1px solid ${COLORS.border}; border-radius: 12px; text-align: center;">
          <a href="${SITE_URL}/asesor/incidencias" style="display: inline-block; padding: 14px 28px; color: ${COLORS.text}; text-decoration: none; font-size: 14px; font-weight: 500; border-radius: 12px;">
            Ver en el Panel →
          </a>
        </td>
      </tr>
    </table>
  `;

  return sendEmail({
    to,
    subject: `[ALERTA ${config.label}] ${alert.rule_name || 'Sistema Externaliza2'}`,
    html: baseEmailTemplate({
      title: 'Alerta',
      content,
      headerColor: config.color,
    }),
  });
}

/**
 * Send sync failure notification email
 */
export async function sendSyncFailureEmail(
  to: string,
  details: { companyCode: string; error: string; syncRunId?: string }
): Promise<EmailResult> {
  const content = `
    <div style="background: #fef2f2; border: 1px solid #fecaca; border-radius: 12px; padding: 20px 24px; margin-bottom: 24px;">
      <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
        <tr>
          <td width="40" style="vertical-align: top;">
            <span style="font-size: 24px;">❌</span>
          </td>
          <td>
            <span style="display: inline-block; background: ${COLORS.error}; color: white; padding: 4px 12px; border-radius: 20px; font-size: 11px; font-weight: 600; letter-spacing: 0.05em; text-transform: uppercase;">
              Sincronización Fallida
            </span>
          </td>
        </tr>
      </table>
    </div>
    
    <h2 style="margin: 0 0 24px; font-family: 'Outfit', sans-serif; font-size: 20px; font-weight: 400; color: ${COLORS.text}; letter-spacing: -0.01em;">
      Error de Sincronización Cegid
    </h2>
    
    <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="margin-bottom: 24px;">
      <tr>
        <td style="padding: 12px 0; border-bottom: 1px solid ${COLORS.border};">
          <span style="font-size: 13px; color: ${COLORS.muted};">Empresa:</span>
          <span style="font-size: 14px; color: ${COLORS.text}; font-weight: 500; margin-left: 8px;">${details.companyCode}</span>
        </td>
      </tr>
      <tr>
        <td style="padding: 12px 0; border-bottom: 1px solid ${COLORS.border};">
          <span style="font-size: 13px; color: ${COLORS.muted};">Fecha:</span>
          <span style="font-size: 14px; color: ${COLORS.text}; margin-left: 8px;">${new Date().toLocaleString('es-ES')}</span>
        </td>
      </tr>
      ${details.syncRunId ? `
      <tr>
        <td style="padding: 12px 0; border-bottom: 1px solid ${COLORS.border};">
          <span style="font-size: 13px; color: ${COLORS.muted};">ID de Sync:</span>
          <span style="font-size: 14px; color: ${COLORS.text}; font-family: monospace; margin-left: 8px;">${details.syncRunId}</span>
        </td>
      </tr>
      ` : ''}
    </table>
    
    <div style="background: #f8fafc; border-radius: 8px; padding: 16px; margin: 24px 0;">
      <p style="margin: 0 0 8px; font-size: 12px; color: ${COLORS.muted}; text-transform: uppercase; letter-spacing: 0.05em;">Error</p>
      <p style="margin: 0; font-size: 14px; color: ${COLORS.error}; font-family: monospace; word-break: break-word;">
        ${details.error}
      </p>
    </div>
    
    <table role="presentation" cellspacing="0" cellpadding="0" border="0" style="margin: 32px 0;">
      <tr>
        <td style="background: ${COLORS.error}; border-radius: 12px; text-align: center;">
          <a href="${SITE_URL}/admin/sincronizacion" style="display: inline-block; padding: 14px 28px; color: ${COLORS.white}; text-decoration: none; font-size: 14px; font-weight: 500; border-radius: 12px;">
            Ver Detalles del Error
          </a>
        </td>
      </tr>
    </table>
  `;

  return sendEmail({
    to,
    subject: `❌ Sync Cegid FALLIDO: ${details.companyCode}`,
    html: baseEmailTemplate({
      title: 'Sync Error',
      content,
      headerColor: COLORS.error,
    }),
  });
}

/**
 * Send GDPR data request confirmation email
 */
export async function sendGDPRConfirmationEmail(
  to: string,
  requestType: 'export' | 'deletion'
): Promise<EmailResult> {
  const titles = {
    export: 'Solicitud de Exportación de Datos',
    deletion: 'Solicitud de Eliminación de Cuenta',
  };

  const bodies = {
    export: `
      <p style="margin: 0 0 16px; font-size: 16px; color: ${COLORS.muted}; line-height: 1.6;">
        Hemos recibido su solicitud de exportación de datos personales conforme al <strong>Artículo 20 del RGPD</strong>.
      </p>
      <p style="margin: 0 0 24px; font-size: 16px; color: ${COLORS.muted}; line-height: 1.6;">
        Recibirá un archivo con todos sus datos en un plazo máximo de <strong>30 días</strong>.
      </p>
    `,
    deletion: `
      <p style="margin: 0 0 16px; font-size: 16px; color: ${COLORS.muted}; line-height: 1.6;">
        Hemos recibido su solicitud de eliminación de cuenta conforme al <strong>Artículo 17 del RGPD</strong> (derecho al olvido).
      </p>
      <p style="margin: 0 0 24px; font-size: 16px; color: ${COLORS.muted}; line-height: 1.6;">
        Su cuenta y datos personales serán eliminados, salvo aquellos que debemos retener por obligación legal:
      </p>
      <ul style="margin: 0 0 24px; padding-left: 20px; color: ${COLORS.muted};">
        <li style="margin-bottom: 8px;">Datos fiscales: 4 años (Ley General Tributaria)</li>
        <li style="margin-bottom: 8px;">Datos contables: 6 años (Código de Comercio)</li>
      </ul>
    `,
  };

  const content = `
    <div style="background: ${COLORS.primaryLight}; border: 1px solid ${COLORS.primary}30; border-radius: 12px; padding: 20px 24px; margin-bottom: 24px;">
      <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
        <tr>
          <td width="40" style="vertical-align: top;">
            <span style="font-size: 24px;">🔒</span>
          </td>
          <td>
            <span style="display: inline-block; background: ${COLORS.primary}; color: white; padding: 4px 12px; border-radius: 20px; font-size: 11px; font-weight: 600; letter-spacing: 0.05em; text-transform: uppercase;">
              RGPD / LOPDGDD
            </span>
          </td>
        </tr>
      </table>
    </div>
    
    <h2 style="margin: 0 0 24px; font-family: 'Outfit', sans-serif; font-size: 22px; font-weight: 300; color: ${COLORS.text}; letter-spacing: -0.01em;">
      ${titles[requestType]}
    </h2>
    
    ${bodies[requestType]}
    
    <div style="background: #fefce8; border-left: 4px solid ${COLORS.warning}; padding: 16px 20px; border-radius: 0 8px 8px 0; margin: 24px 0;">
      <p style="margin: 0; font-size: 14px; color: ${COLORS.text}; line-height: 1.5;">
        <strong>¿No ha realizado esta solicitud?</strong><br>
        Contacte inmediatamente con <a href="mailto:administracion@externaliza2.com" style="color: ${COLORS.primary}; text-decoration: none;">administracion@externaliza2.com</a>
      </p>
    </div>
  `;

  return sendEmail({
    to,
    subject: `${titles[requestType]} - Externaliza2`,
    html: baseEmailTemplate({
      title: titles[requestType],
      content,
      footer: 'Externaliza2 Consultores S.L. — Cumplimiento RGPD y LOPDGDD',
    }),
  });
}

/**
 * Send password reset email
 */
export async function sendPasswordResetEmail(
  to: string,
  resetUrl: string
): Promise<EmailResult> {
  const content = `
    <h2 style="margin: 0 0 16px; font-family: 'Outfit', sans-serif; font-size: 24px; font-weight: 300; color: ${COLORS.text}; letter-spacing: -0.02em;">
      Restablecer Contraseña
    </h2>
    
    <p style="margin: 0 0 24px; font-size: 16px; color: ${COLORS.muted}; line-height: 1.6;">
      Has solicitado restablecer tu contraseña. Haz clic en el botón de abajo para crear una nueva contraseña:
    </p>
    
    <table role="presentation" cellspacing="0" cellpadding="0" border="0" style="margin: 32px 0;">
      <tr>
        <td style="background: linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%); border-radius: 12px; text-align: center;">
          <a href="${resetUrl}" style="display: inline-block; padding: 16px 32px; color: ${COLORS.white}; text-decoration: none; font-size: 15px; font-weight: 500; border-radius: 12px;">
            Restablecer Contraseña
          </a>
        </td>
      </tr>
    </table>
    
    <p style="margin: 0 0 16px; font-size: 14px; color: ${COLORS.muted}; line-height: 1.5;">
      Si el botón no funciona, copia y pega este enlace en tu navegador:
    </p>
    
    <p style="margin: 0 0 24px; padding: 12px; background: ${COLORS.background}; border-radius: 8px; font-size: 13px; color: ${COLORS.muted}; word-break: break-all; font-family: monospace;">
      ${resetUrl}
    </p>
    
    <div style="background: #fefce8; border-left: 4px solid ${COLORS.warning}; padding: 16px 20px; border-radius: 0 8px 8px 0; margin: 24px 0;">
      <p style="margin: 0; font-size: 13px; color: ${COLORS.text}; line-height: 1.5;">
        <strong>⚠️ Importante:</strong> Este enlace expira en 1 hora. Si no solicitaste este cambio, ignora este email.
      </p>
    </div>
  `;

  return sendEmail({
    to,
    subject: 'Restablecer tu contraseña - Externaliza2',
    html: baseEmailTemplate({
      title: 'Restablecer Contraseña',
      content,
    }),
  });
}

/**
 * Send daily/weekly summary email
 */
export async function sendSummaryEmail(
  to: string,
  summary: {
    period: 'daily' | 'weekly';
    metrics: {
      newDocuments: number;
      processedByAI: number;
      alerts: number;
      syncStatus: 'success' | 'partial' | 'error';
    };
  }
): Promise<EmailResult> {
  const periodLabels = {
    daily: 'Resumen Diario',
    weekly: 'Resumen Semanal',
  };

  const statusConfig = {
    success: { color: COLORS.success, label: '✓ Exitosa' },
    partial: { color: COLORS.warning, label: '⚠ Parcial' },
    error: { color: COLORS.error, label: '✗ Error' },
  };

  const status = statusConfig[summary.metrics.syncStatus];

  const content = `
    <h2 style="margin: 0 0 8px; font-family: 'Outfit', sans-serif; font-size: 24px; font-weight: 300; color: ${COLORS.text}; letter-spacing: -0.02em;">
      ${periodLabels[summary.period]}
    </h2>
    <p style="margin: 0 0 32px; font-size: 14px; color: ${COLORS.muted};">
      ${new Date().toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
    </p>
    
    <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="margin-bottom: 32px;">
      <tr>
        <td width="48%" style="background: ${COLORS.primaryLight}; border-radius: 12px; padding: 20px; text-align: center;">
          <p style="margin: 0 0 8px; font-size: 32px; font-weight: 300; color: ${COLORS.primary}; font-family: 'Outfit', sans-serif;">
            ${summary.metrics.newDocuments}
          </p>
          <p style="margin: 0; font-size: 12px; color: ${COLORS.muted}; text-transform: uppercase; letter-spacing: 0.05em;">Documentos Nuevos</p>
        </td>
        <td width="4%"></td>
        <td width="48%" style="background: ${COLORS.secondaryLight}; border-radius: 12px; padding: 20px; text-align: center;">
          <p style="margin: 0 0 8px; font-size: 32px; font-weight: 300; color: ${COLORS.secondary}; font-family: 'Outfit', sans-serif;">
            ${summary.metrics.processedByAI}
          </p>
          <p style="margin: 0; font-size: 12px; color: ${COLORS.muted}; text-transform: uppercase; letter-spacing: 0.05em;">Procesados por IA</p>
        </td>
      </tr>
    </table>
    
    <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="margin-bottom: 32px;">
      <tr>
        <td width="48%" style="background: ${summary.metrics.alerts > 0 ? '#fef2f2' : '#f0fdf4'}; border-radius: 12px; padding: 20px; text-align: center;">
          <p style="margin: 0 0 8px; font-size: 32px; font-weight: 300; color: ${summary.metrics.alerts > 0 ? COLORS.error : COLORS.success}; font-family: 'Outfit', sans-serif;">
            ${summary.metrics.alerts}
          </p>
          <p style="margin: 0; font-size: 12px; color: ${COLORS.muted}; text-transform: uppercase; letter-spacing: 0.05em;">Alertas</p>
        </td>
        <td width="4%"></td>
        <td width="48%" style="background: ${COLORS.background}; border: 1px solid ${COLORS.border}; border-radius: 12px; padding: 20px; text-align: center;">
          <p style="margin: 0 0 8px; font-size: 14px; font-weight: 500; color: ${status.color};">
            ${status.label}
          </p>
          <p style="margin: 0; font-size: 12px; color: ${COLORS.muted}; text-transform: uppercase; letter-spacing: 0.05em;">Sync Cegid</p>
        </td>
      </tr>
    </table>
    
    <table role="presentation" cellspacing="0" cellpadding="0" border="0" style="margin: 32px 0;">
      <tr>
        <td style="border: 1px solid ${COLORS.border}; border-radius: 12px; text-align: center;">
          <a href="${SITE_URL}" style="display: inline-block; padding: 14px 28px; color: ${COLORS.text}; text-decoration: none; font-size: 14px; font-weight: 500; border-radius: 12px;">
            Ver Panel Completo →
          </a>
        </td>
      </tr>
    </table>
  `;

  return sendEmail({
    to,
    subject: `${periodLabels[summary.period]} - Externaliza2`,
    html: baseEmailTemplate({
      title: periodLabels[summary.period],
      content,
    }),
  });
}

// Export colors for external use
export { COLORS };
